import { Injectable } from '@angular/core';

export type Frequency =
  | 'Monthly'
  | 'Quarterly'
  | 'Annually'
  | 'Semiannually'
  | 'One-Time';

export interface FrequencyOffsetOption {
  label: string;
  value: string;
}

@Injectable({ providedIn: 'root' })
export class FrequencyOptionsService {
  private options: Record<Frequency, FrequencyOffsetOption[]> = {
    Monthly: [
      { label: '10 days after end of each Calendar Month', value: '10days' },
      { label: '15 days after end of each Calendar Month', value: '15days' },
      { label: '20 days after end of each Calendar Month', value: '20days' },
      { label: '25 days after end of each Calendar Month', value: '25days' },
      { label: '30 days after end of each Calendar Month', value: '30days' },
      { label: '35 days after end of each Calendar Month', value: '35days' },
      { label: '45 days after end of each Calendar Month', value: '45days' },
    ],
    Quarterly: [
      { label: 'End of each Calendar Quarter', value: 'endQuarter' },
      { label: '25 days after end of each Calendar Quarter', value: '25days' },
      { label: '30 days after end of each Calendar Quarter', value: '30days' },
      { label: '35 days after end of each Calendar Quarter', value: '35days' },
      {
        label: '45 days after end of each Calendar / Fiscal Quarter',
        value: '45daysFiscal',
      },
      { label: '50 days after end of each Calendar Quarter', value: '50days' },
      { label: '55 days after end of each Calendar Quarter', value: '55days' },
      {
        label: '45 days after end of each Calendar Quarter (except 4th)',
        value: '45daysExcept4th',
      },
      {
        label: '45 days after end of each Fiscal Quarter (incl. year-end)',
        value: '45daysFiscalInclYearEnd',
      },
      {
        label: '60 days after end of each Calendar / Fiscal Quarter',
        value: '60daysFiscal',
      },
      {
        label: '60 days after end of each Quarter (except 4th)',
        value: '60daysExcept4th',
      },
      {
        label: '75 days after the last day of each Fiscal Quarter',
        value: '75daysFiscal',
      },
      {
        label: '75 days after the last day of each of the first 3 Cal. Qtrs',
        value: '75daysFirst3',
      },
      { label: '90 days after end of Quarter', value: '90days' },
      { label: '120 days after end of Quarter', value: '120days' },
    ],
    Annually: [
      { label: '210 days after close of each Fiscal Year', value: '210days' },
      { label: '180 days after close of each Fiscal Year', value: '180days' },
      { label: '150 days after close of each Fiscal Year', value: '150days' },
      { label: '130 days after close of each Fiscal Year', value: '130days' },
      { label: '120 days after close of each Fiscal Year', value: '120days' },
      { label: '90 days after end of each Fiscal Year', value: '90days' },
      { label: '85 days after end of each Fiscal Year', value: '85days' },
      { label: '60 days after end of Fiscal Year', value: '60days' },
      { label: '60 days prior to end of Fiscal Year', value: '60daysPrior' },
      {
        label: '30 days after start of each Fiscal Year',
        value: '30daysAfterStart',
      },
      { label: '30 days prior to end of Fiscal Year', value: '30daysPrior' },
      { label: '45 days after close of Fiscal Year', value: '45days' },
      {
        label: '45 days before the start of Fiscal Year',
        value: '45daysBeforeStart',
      },
      { label: 'Dec. 1', value: 'dec1' },
      { label: 'Dec. 31', value: 'dec31' },
      { label: 'Jan. 1', value: 'jan1' },
      { label: 'Mar. 31', value: 'mar31' },
      {
        label: 'After the completion of filing Tax Returns',
        value: 'afterTaxReturn',
      },
    ],
    Semiannually: [
      { label: 'Not later than Aug 31 of each Fiscal Year', value: 'aug31' },
      { label: 'Not later than Feb 28 of each Calendar Year', value: 'feb28' },
      {
        label: '60 days after end of 2nd & 4th of each Calendar Quarter',
        value: '60days2nd4th',
      },
      {
        label: '45 days after end of 2nd & 4th of each Calendar Quarter',
        value: '45days2nd4th',
      },
      {
        label: 'Each Feb 1st and Aug 1st of each Fiscal Year',
        value: 'feb1aug1',
      },
    ],
    'One-Time': [{ label: 'A date can be entered.', value: 'customDate' }],
  };

  getFrequencyOptions(): Frequency[] {
    return Object.keys(this.options) as Frequency[];
  }

  getFrequencyOffsetOptions(frequency: Frequency): FrequencyOffsetOption[] {
    return this.options[frequency] || [];
  }

  getTenor(frequency: Frequency): string {
    switch (frequency) {
      case 'Monthly':
        return 'Monthly';
      case 'Quarterly':
        return 'Quarterly';
      case 'Semiannually':
        return 'SemiAnnual';
      case 'Annually':
        return 'Annual';
      default:
        return 'None';
    }
  }

  // Example: Calculate first due date (stub, needs implementation for each rule)
  getFirstDueDate(
    frequency: Frequency,
    ruleValue: string,
    referenceDate: Date
  ): Date | null {
    // Ensure referenceDate is a Date object
    if (!(referenceDate instanceof Date)) {
      referenceDate = new Date(referenceDate);
    }
    // console.log(
    //   `Calculating first due date for frequency: ${frequency}, ruleValue: ${ruleValue}, referenceDate: ${referenceDate}`
    // );
    const clone = (date: Date) => new Date(date.getTime());

    function addDays(date: Date, days: number) {
      const d = new Date(date);
      d.setDate(d.getDate() + days);
      return d;
    }

    switch (frequency) {
      case 'Monthly': {
        let year = referenceDate.getFullYear();
        let month = referenceDate.getMonth();
        let candidate: Date;
        let days = 0;
        switch (ruleValue) {
          case '10days':
            days = 10;
            break;
          case '15days':
            days = 15;
            break;
          case '20days':
            days = 20;
            break;
          case '25days':
            days = 25;
            break;
          case '30days':
            days = 30;
            break;
          case '35days':
            days = 35;
            break;
          case '45days':
            days = 45;
            break;
          default:
            return null;
        }
        do {
          // Start of current month
          candidate = new Date(year, month, 0);
          candidate = addDays(candidate, days);
          if (candidate > referenceDate) return candidate;
          // Move to next month
          month++;
          if (month > 11) {
            month = 0;
            year++;
          }
        } while (true);
      }

      case 'Quarterly': {
        let year = referenceDate.getFullYear();
        let month = referenceDate.getMonth();
        let candidate: Date;
        let days = 0;
        let ruleHandled = true;
        let label = ruleValue;
        switch (ruleValue) {
          case 'endQuarter':
            days = 0;
            break;
          case '25days':
            days = 25;
            break;
          case '30days':
            days = 30;
            break;
          case '35days':
            days = 35;
            break;
          case '45daysFiscal':
          case '45daysExcept4th':
          case '45daysFiscalInclYearEnd':
            days = 45;
            break;
          case '50days':
            days = 50;
            break;
          case '55days':
            days = 55;
            break;
          case '60daysFiscal':
          case '60daysExcept4th':
            days = 60;
            break;
          case '75daysFiscal':
          case '75daysFirst3':
            days = 75;
            break;
          case '90days':
            days = 90;
            break;
          case '120days':
            days = 120;
            break;
          default:
            ruleHandled = false;
        }
        if (!ruleHandled) return null;
        do {
          // Find end of current quarter
          const quarter = Math.floor(month / 3);
          const quarterEndMonth = quarter * 3 + 2;
          candidate = new Date(year, quarterEndMonth + 1, 0);
          candidate = addDays(candidate, days);
          if (candidate > referenceDate) return candidate;
          // Move to next quarter
          month += 3;
          if (month > 11) {
            month = month % 12;
            year++;
          }
        } while (true);
      }

      case 'Annually': {
        let year = referenceDate.getFullYear();
        let candidate: Date;
        let days = 0;
        switch (ruleValue) {
          case '210days':
            days = 210;
            break;
          case '180days':
            days = 180;
            break;
          case '150days':
            days = 150;
            break;
          case '130days':
            days = 130;
            break;
          case '120days':
            days = 120;
            break;
          case '90days':
            days = 90;
            break;
          case '85days':
            days = 85;
            break;
          case '60days':
            days = 60;
            break;
          case '60daysPrior':
            days = -60;
            break;
          case '30daysPrior':
            days = -30;
            break;
          case '45days':
            days = 45;
            break;
          default:
            days = 0;
        }
        if (['dec1', 'dec31', 'jan1', 'mar31'].includes(ruleValue)) {
          do {
            switch (ruleValue) {
              case 'dec1':
                candidate = new Date(year, 11, 1);
                break;
              case 'dec31':
                candidate = new Date(year, 11, 31);
                break;
              case 'jan1':
                candidate = new Date(year, 0, 1);
                break;
              case 'mar31':
                candidate = new Date(year, 2, 31);
                break;
            }
            if (candidate > referenceDate) return candidate;
            year++;
          } while (true);
        }
        if (
          ruleValue === '30daysAfterStart' ||
          ruleValue === '45daysBeforeStart'
        ) {
          do {
            const fiscalYearStart = new Date(year, 0, 1);
            candidate = new Date(fiscalYearStart);
            if (ruleValue === '30daysAfterStart')
              candidate.setDate(candidate.getDate() + 30);
            if (ruleValue === '45daysBeforeStart')
              candidate.setDate(candidate.getDate() - 45);
            if (candidate > referenceDate) return candidate;
            year++;
          } while (true);
        }
        // Default: add days to fiscal year end
        do {
          const fiscalYearEnd = new Date(year, 11, 31);
          candidate = addDays(fiscalYearEnd, days);
          if (candidate > referenceDate) return candidate;
          year++;
        } while (true);
      }

      case 'Semiannually': {
        let year = referenceDate.getFullYear();
        let candidate: Date;
        switch (ruleValue) {
          case 'aug31':
            do {
              candidate = new Date(year, 7, 31);
              if (candidate > referenceDate) return candidate;
              year++;
            } while (true);
          case 'feb28':
            do {
              candidate = new Date(year, 1, 28);
              if (candidate > referenceDate) return candidate;
              year++;
            } while (true);
          // Add more rules as needed
        }
        break;
      }

      case 'One-Time':
        if (ruleValue === 'customDate') return clone(referenceDate);
        break;
    }
    return null;
  }
}
